import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { estimationsApi } from '../api/estimations';

export default function Estimations() {
  const [estimations, setEstimations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadEstimations();
  }, [page, statusFilter]);

  const loadEstimations = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 15 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const response = await estimationsApi.list(params);
      setEstimations(response.data.estimations);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error loading estimations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadEstimations();
  };

  const complexityColor = (c) => {
    const colors = { LOW: 'bg-green-100 text-green-700', MEDIUM: 'bg-yellow-100 text-yellow-700', HIGH: 'bg-red-100 text-red-700' };
    return colors[c] || 'bg-gray-100 text-gray-700';
  };

  const statusColor = (s) => {
    const colors = { Activo: 'text-green-600', Completado: 'text-blue-600', Archivado: 'text-gray-400' };
    return colors[s] || 'text-gray-600';
  };

  const totalPages = Math.ceil(total / 15);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Estimaciones</h1>
        <Link
          to="/estimations/new"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          + Nueva Estimación
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <form onSubmit={handleSearch} className="flex gap-3 items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre de proyecto..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Todos los estados</option>
            <option value="Activo">Activo</option>
            <option value="Completado">Completado</option>
            <option value="Archivado">Archivado</option>
          </select>
          <button
            type="submit"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
          >
            Buscar
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>
        ) : estimations.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p>No se encontraron estimaciones.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Proyecto</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Complejidad</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Horas</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Estado</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Propietario</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Fecha</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {estimations.map((est) => (
                <tr key={est.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/estimations/${est.id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800 truncate max-w-xs">{est.project_name}</span>
                      {est.shared_with_me && (
                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                          {est.shared_with_me}
                        </span>
                      )}
                      {est.comments_count > 0 && (
                        <span className="text-xs text-gray-400">💬 {est.comments_count}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${complexityColor(est.complexity)}`}>
                      {est.complexity}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{est.total_hours}h</td>
                  <td className={`px-4 py-3 font-medium ${statusColor(est.status)}`}>{est.status}</td>
                  <td className="px-4 py-3 text-gray-600">{est.owner_username}</td>
                  <td className="px-4 py-3 text-gray-500">{new Date(est.created_at).toLocaleDateString('es-MX')}</td>
                  <td className="px-4 py-3">
                    <Link to={`/estimations/${est.id}`} className="text-blue-600 hover:text-blue-700 text-xs">
                      Ver →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Mostrando {(page - 1) * 15 + 1}–{Math.min(page * 15, total)} de {total}
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-gray-50"
              >
                ←
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-gray-50"
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
